/**
 * CustomPriceRange — single-handle max-price slider.
 *
 * Data attributes (Shopify passes values in cents):
 *   data-max             {Number}  filter.range_max in cents
 *   data-current-max     {Number}  currently applied max in cents (equals data-max when unset)
 *   data-max-name        {String}  URL param name for the max price
 *   data-currency-symbol {String}  e.g. "€"
 *
 * The element divides cents by 100 for display and form submission.
 * The hidden input is disabled when the slider is at maximum (no active filter),
 * keeping the URL clean.
 */
if (!customElements.get('custom-price-range')) {
  class CustomPriceRange extends HTMLElement {
    connectedCallback() {
      const currencySymbol = this.dataset.currencySymbol || '';
      const maxCents     = parseInt(this.dataset.max, 10)        || 0;
      const currentCents = parseInt(this.dataset.currentMax, 10) || maxCents;
      const maxName      = this.dataset.maxName;

      // Convert cents → whole currency units for display & submission
      const displayMax     = Math.ceil(maxCents / 100);
      const displayCurrent = Math.min(Math.round(currentCents / 100), displayMax);

      // 5-tick scale: 0 % · 25 % · 50 % · 75 % · 100 %
      const scaleHTML = [0, 0.25, 0.5, 0.75, 1]
        .map((p) => `<span><span class="custom-price-range__tick-scale"></span><span>${currencySymbol}${Math.round(displayMax * p)}</span></span>`)
        .join('');

      this.innerHTML = `
        <div class="custom-price-range__display hidden">
          <span class="custom-price-range__value">
            ${currencySymbol}0 &ndash; ${currencySymbol}<output class="custom-price-range__output">${displayCurrent}</output>
          </span>
        </div>
        <div class="custom-price-range__slider-wrapper">
          <div class="custom-price-range__track-bg"></div>
          <div class="custom-price-range__track-fill"></div>
          <input
            type="range"
            class="custom-price-range__thumb"
            min="0"
            max="${displayMax}"
            value="${displayCurrent}"
            step="1"
            aria-label="Maximum price"
          >
        </div>
        <div class="custom-price-range__scale">${scaleHTML}</div>
        <input type="hidden" class="custom-price-range__hidden" name="${maxName}">
      `;

      this._displayMax  = displayMax;
      this.thumb        = this.querySelector('.custom-price-range__thumb');
      this.output       = this.querySelector('.custom-price-range__output');
      this.trackFill    = this.querySelector('.custom-price-range__track-fill');
      this.hiddenMax    = this.querySelector('.custom-price-range__hidden');

      this._syncHidden(displayCurrent);
      this._updateTrack(displayCurrent);

      this.thumb.addEventListener('input', this._onInput.bind(this));
    }

    _onInput() {
      const val = parseInt(this.thumb.value, 10);
      this.output.textContent = val;
      this._syncHidden(val);
      this._updateTrack(val);
    }

    // Disable hidden input when at max so the param doesn't appear in the URL
    _syncHidden(val) {
      if (val < this._displayMax) {
        this.hiddenMax.removeAttribute('disabled');
        this.hiddenMax.value = val;
      } else {
        this.hiddenMax.setAttribute('disabled', '');
        this.hiddenMax.value = '';
      }
    }

    _updateTrack(val) {
      const pct = this._displayMax > 0 ? (val / this._displayMax) * 100 : 0;
      this.trackFill.style.width = `${pct}%`;
    }
  }

  customElements.define('custom-price-range', CustomPriceRange);
}

// =============================================================================
// Facets panel toggle (+ / − buttons in .custom-facets__heading)
// Default state: open. Clicking − closes the panel; clicking + opens it.
// =============================================================================
(function initFacetsToggle() {
  function setup(form) {
    const btnPlus  = form.querySelector('.custom-facets__heading-button--plus');
    const btnMinus = form.querySelector('.custom-facets__heading-button--minus');
    const content  = form.querySelector('.custom-facets__content');

    if (!btnPlus || !btnMinus || !content) return;

    btnMinus.addEventListener('click', () => form.classList.add('is-closed'));
    btnPlus.addEventListener('click',  () => form.classList.remove('is-closed'));
  }

  // Run immediately (script is deferred, DOM is ready)
  document.querySelectorAll('.custom-facets__form').forEach(setup);
})();
